import React, { memo, useEffect, useState, useRef } from "react";
import { array, bool, func, number, object } from "prop-types";
import { Line, Circle } from "react-konva";
import {
  getCurrentShapes,
  selectSelectedPoints,
  selectCurrentPolyonPointIndex,
} from "redux/shape/selector";
import {
  setSelectedPointsPolygon,
  setCurrentPolygonPointIndex,
} from "redux/shape/actions";
import { changeOpacityOfColor } from "helpers";
import { useSelector, useDispatch } from "react-redux";

const width = 5;
function Polygon({
  polygon = {},
  handleDragStartPoint,
  onChange,
  isFinished = true,
  ratio,
  curMousePos,
  onSelect,
  isSelected,
  permissionEdit = false,
  draggable: draggableProp,
  ...rest
}) {
  const dispatch = useDispatch();
  const polygonRef = useRef();
  const [polygonProps, setPolygonProps] = useState(polygon);
  const [tempPoint1st, setTempPoint1st] = useState([0, 0]);
  const [tempPoint2nd, setTempPoint2nd] = useState([0, 0]);
  const [currentPointState, setCurrentPointState] = useState(-1);
  const currentShape = useSelector(getCurrentShapes);
  const pointRef = useRef([]);
  const listSelectedPoints = useSelector(selectSelectedPoints);
  const currentPolygonPoint = useSelector(selectCurrentPolyonPointIndex);

  const [dragPoints, setDragPoints] = useState([]);

  useEffect(() => {
    setPolygonProps(polygon);
  }, [polygon]);

  useEffect(() => {
    dispatch(setSelectedPointsPolygon([]));
    setTempPoint1st([0, 0]);
    setTempPoint2nd([0, 0]);
  }, [currentShape]);

  const handleSetCurrentPoint = (index) => {
    setCurrentPointState(index);
    dispatch(setCurrentPolygonPointIndex(index));
  };

  const handleDragMovePoint = (event) => {
    if (currentPolygonPoint > 0) {
      setTempPoint1st([0, 0]);
      setTempPoint2nd([0, 0]);
    }
    const pos = [event.target.attrs.x / ratio, event.target.attrs.y / ratio];
    const index = event.target.attrs.index;
    setPolygonProps({
      ...polygon,
      points: [
        ...polygon.points.slice(0, index),
        pos,
        ...polygon.points.slice(index + 1),
      ],
    });

    // if user choose one point of current polygon
    // update coordinate of two temp point
    // if (isSelected && currentPointState > 0) {
    //   console.log(currentPointState);
    //   handleClickPoint(currentPointState)();
    // }
  };

  const handleDragEndPoint = (index) => (event) => {
    const stage = event.target.getStage();
    const { x, y } = stage.getPointerPosition();
    const listPoint = [...polygonProps.points];
    listPoint.splice(index, 1, [x / ratio, y / ratio]);

    onChange && onChange({ ...polygonProps, dataPoint: listPoint });
  };

  const getCenterPointOfTwoPoints = (point1, point2) => {
    const [x1, y1] = point1;
    const [x2, y2] = point2;
    return [(x1 + x2) / 2, (y1 + y2) / 2];
  };

  const handleClickPoint = (index) => () => {
    if (isFinished) {
      const listPoint = polygonProps.points;
      if (isSelected) {
        handleSetCurrentPoint(index);
        if (listSelectedPoints.includes(index)) {
          if (index === pointRef.current[pointRef.current.length - 1]) {
            pointRef.current = pointRef.current.filter(
              (item) => item !== index
            );
            if (pointRef.current.length === 0) {
              setTempPoint1st([0, 0]);
              setTempPoint2nd([0, 0]);
            } else {
              const beforeIndex =
                pointRef.current[pointRef.current.length - 1] - 1 >= 0
                  ? pointRef.current[pointRef.current.length - 1] - 1
                  : listPoint.length - 1;
              const afterIndex =
                pointRef.current[pointRef.current.length - 1] + 1 <
                listPoint.length
                  ? pointRef.current[pointRef.current.length - 1] + 1
                  : 0;
              setTempPoint1st(
                getCenterPointOfTwoPoints(
                  listPoint[pointRef.current[pointRef.current.length - 1]],
                  listPoint[beforeIndex]
                )
              );
              setTempPoint2nd(
                getCenterPointOfTwoPoints(
                  listPoint[pointRef.current[pointRef.current.length - 1]],
                  listPoint[afterIndex]
                )
              );
            }
          } else {
            pointRef.current = pointRef.current.filter(
              (item) => item !== index
            );
          }
        } else {
          const beforeIndex = index - 1 >= 0 ? index - 1 : listPoint.length - 1;
          const afterIndex = index + 1 < listPoint.length ? index + 1 : 0;
          pointRef.current = [...pointRef.current, index];
          setTempPoint1st(
            getCenterPointOfTwoPoints(listPoint[index], listPoint[beforeIndex])
          );
          setTempPoint2nd(
            getCenterPointOfTwoPoints(listPoint[index], listPoint[afterIndex])
          );
        }
        const listPointsUpdated = listSelectedPoints.includes(index)
          ? listSelectedPoints.filter((item) => item !== index)
          : [...listSelectedPoints, index];
        dispatch(setSelectedPointsPolygon(listPointsUpdated));
      }
    }
  };

  const checkIsSelectedPoints = (index) =>
    isSelected && listSelectedPoints.includes(index);

  const handleClickTempPoint = (point) => () => {
    const listPoint = [...polygonProps.points];
    const currentIndex = currentPointState;
    const beforeIndex =
      currentIndex - 1 >= 0 ? currentIndex - 1 : listPoint.length - 1;
    const realIndex =
      JSON.stringify(
        getCenterPointOfTwoPoints(
          listPoint[currentIndex],
          listPoint[beforeIndex]
        )
      ) === JSON.stringify(point)
        ? beforeIndex + 1
        : currentIndex + 1;
    listPoint.splice(realIndex, 0, point);
    onChange &&
      onChange({
        ...polygonProps,
        points: listPoint,
      });
    setTempPoint1st([0, 0]);
    setTempPoint2nd([0, 0]);
    dispatch(setSelectedPointsPolygon([]));
    handleSetCurrentPoint(-1);
  };

  const flattenedPoints = (points) =>
    points
      .concat(
        isFinished
          ? []
          : curMousePos
          ? curMousePos.map((item) => item / ratio)
          : []
      )
      .reduce((a, b) => a.concat(b), []);

  const handleDragMoveLine = () => {
    const node = polygonRef.current;
    const newPoints = dragPoints.map((item) => [
      item[0] + node.x() / ratio,
      item[1] + node.y() / ratio,
    ]);
    node.x(0);
    node.y(0);

    setPolygonProps({
      ...polygonProps,
      points: [...newPoints],
    });
  };

  const handleDragStartLine = () => {
    setDragPoints(polygonProps.points);
  };

  const handleDragEndLine = () => {
    onChange && onChange(polygonProps);
    setDragPoints([]);
  };

  return (
    <>
      <Line
        points={flattenedPoints(polygonProps.points).map(
          (item) => item * ratio
        )}
        onClick={onSelect}
        stroke={`${changeOpacityOfColor(
          polygonProps.color,
          isSelected || !isFinished ? 0.8 : 0.5
        )}`}
        fill={`${changeOpacityOfColor(polygonProps.color, 0.3)}`}
        strokeWidth={2}
        closed
        {...rest}
        draggable={permissionEdit && draggableProp}
        onDragMove={handleDragMoveLine}
        onDragStart={handleDragStartLine}
        onDragEnd={handleDragEndLine}
        ref={polygonRef}
      />
      {(isSelected || !isFinished) &&
        polygonProps.points.map((point, index) => {
          const x = point[0];
          const y = point[1];
          return (
            <Circle
              index={index}
              key={index}
              x={x * ratio}
              y={y * ratio}
              width={width}
              height={width}
              fill={checkIsSelectedPoints(index) ? "white" : polygon.color}
              stroke={checkIsSelectedPoints(index) ? "white" : polygon.color}
              radius={3}
              onDragStart={handleDragStartPoint}
              onDragMove={handleDragMovePoint}
              onDragEnd={handleDragEndPoint(index)}
              draggable={permissionEdit}
              onClick={handleClickPoint(index)}
            />
          );
        })}

      {isSelected && currentPolygonPoint >= 0 && (
        <>
          <Circle
            x={tempPoint1st[0] * ratio}
            y={tempPoint1st[1] * ratio}
            stroke="grey"
            fill="white"
            width={width}
            height={width}
            radius={4}
            onClick={handleClickTempPoint(tempPoint1st, -1)}
            listening={permissionEdit}
          />
          <Circle
            x={tempPoint2nd[0] * ratio}
            y={tempPoint2nd[1] * ratio}
            stroke="grey"
            fill="white"
            width={width}
            height={width}
            radius={4}
            onClick={handleClickTempPoint(tempPoint2nd, 1)}
            listening={permissionEdit}
          />
        </>
      )}
    </>
  );
}
Polygon.propTypes = {
  ratio: number,
  polygon: object,
  isFinished: bool,
  handleDragStartPoint: func,
  onChange: func,
  curMousePos: array,
  onSelect: func,
  isSelected: bool,
  permissionEdit: bool,
  draggable: bool,
};

export default memo(Polygon);
